/**
 * W8-HELP-CENTER (D448, ADR-0079) — the pure view model behind the support queue, the ticket's public thread and the SOS
 * alert of the console (findings S-02, S-03, A-12).
 *
 * - {@link queueStatusParam}: the queue's status filter → the server's `?status=` (the OPEN tickets are the server's
 *   default, so the filter "open" sends nothing).
 * - {@link slaBadge}: a queue row's SLA as one badge — overdue (danger), due within the hour (warning), running (neutral),
 *   met (success). It reads the SERVER's `slaState` (never recomputes a due time the server did not send).
 * - {@link newSosIncidents} / {@link sosAlertMarkets}: the SOS alert (A-12) — which incidents are NEW critical alerts since
 *   the last read (the sound and the browser notification fire once per incident), and which markets the alert reads: the
 *   city in scope, or EVERY city of the country in scope that the operator's grants reach (before D448 a country-scoped
 *   operator heard nothing at all).
 *
 * Neutral: statuses, tones and codes are data; no country, language or time zone.
 */
import { type OperatingMarketView } from '@vello/api-client';
import { marketsOfRegion } from './scope.js';
import { type ScopeState } from './shell.js';

export { QUEUE_STATUS_FILTERS, type QueueStatusFilter, queueStatusParam, type SlaBadgeTone, type SlaBadge, slaBadge, dueInParts } from '@vello/support-desk-ux';

/** The incident fields the SOS alert reads. */
export interface AlertIncident {
  readonly incidentId: string;
  readonly type: string;
  readonly severity: string;
  readonly status: string;
}

/**
 * The incidents that should ring NOW: open SOS or CRITICAL ones not seen before. The first read of a session seeds the
 * "seen" set without ringing (an operator opening the console is not alerted about yesterday's queue — the bell still
 * counts them); every later read rings once per new incident.
 */
export function newSosIncidents(seen: ReadonlySet<string> | undefined, incidents: readonly AlertIncident[]): { readonly ring: readonly AlertIncident[]; readonly seen: ReadonlySet<string> } {
  const urgent = incidents.filter((i) => (i.type === 'SOS' || i.severity === 'CRITICAL') && i.status !== 'RESOLVED' && i.status !== 'CLOSED');
  const next = new Set([...(seen ?? []), ...urgent.map((i) => i.incidentId)]);
  if (seen === undefined) return { ring: [], seen: next };
  return { ring: urgent.filter((i) => !seen.has(i.incidentId)), seen: next };
}

/**
 * The markets the SOS alert reads: the city in scope; at state scope, the state's markets the grants reach; at country scope, every city of that country the operator's own
 * grants reach (the scope selector's allowed set fused with the market catalog); at global scope, every catalog city of
 * the grants. Sorted, deduplicated, and bounded (the alert never fans out to more than `max` reads).
 */
export function sosAlertMarkets(scope: ScopeState, markets: readonly OperatingMarketView[], max = 20): readonly string[] {
  if (scope.current.city !== undefined) return [scope.current.city];
  const country = scope.current.country;
  const granted = new Set(scope.allowed.map((s) => s.city).filter((c): c is string => c !== undefined));
  // A state entry (`region` set, no city) is NOT a country grant: it reaches only its own markets, which are already in `granted`.
  const grantsCountry = (cc: string): boolean => scope.allowed.some((s) => s.city === undefined && s.region === undefined && (s.country === undefined || s.country === cc));
  const inState = scope.current.region !== undefined ? new Set(marketsOfRegion(scope.current.region, markets).map((m) => m.id)) : undefined;
  const ids = markets
    .filter((m) => (country === undefined || m.countryCode === country) && (inState === undefined || inState.has(m.id)) && (granted.has(m.id) || grantsCountry(m.countryCode)))
    .map((m) => m.id);
  return [...new Set(ids)].sort().slice(0, max);
}
